/**
 * `@dxf-ai-editor/drawing-model` — the ONE-TRUTH WIDTH RESOLUTION of a
 * width-carrying polyline: which DXF group means what, and what a vertex that
 * declares none inherits.
 *
 * # Why here and not in a renderer folder
 *
 * This is not rendering. It is the same class of question
 * `./bulgeArc.ts` answers about group 42 — a pure reading of the entity
 * schemas defined in this package (`lwpolylineEntitySchema`,
 * `polylineEntitySchema`) — and it had grown THREE independent
 * implementations, one per consumer that happened to need it: the canvas's
 * LWPOLYLINE adapter, the canvas's heavy-POLYLINE adapter and the vector-PDF
 * plot's band builder. A test pinned two of them to each other; nothing
 * pinned the third, and a test is not an implementation.
 *
 * `drawing-model` rather than `cad-core` deliberately. `cad-core` depends on
 * `drawing-model`, never the reverse (see this package's `index.ts` notes on
 * the other convergences), so a rule sited in `cad-core` is unreachable from
 * `drawing-model`'s OWN WCS bounds walk (`wcsBoundsLeaf.ts`), which bounds a
 * wide polyline's centreline today and is the next consumer that will want the
 * authored width. Sited here it is reachable from every consumer that exists —
 * the two scene3d adapters, the plot, `cad-core`'s picker/topology and the
 * bounds walk — with no layering inversion available to a later caller.
 *
 * # The three width channels (Autodesk DXF reference)
 *
 *   - **LWPOLYLINE** — per-vertex `startWidth` / `endWidth` (groups 40 / 41)
 *     describe the span STARTING at that vertex. Group 43 `constantWidth` is
 *     the entity-level uniform width and is documented as "Constant width
 *     (optional; default = 0). **Not used if variable width (codes 40 and/or
 *     41) is set**", so the two channels are MUTUALLY EXCLUSIVE and the choice
 *     between them is made ONCE PER ENTITY, never per side of a vertex: if any
 *     vertex declares a non-zero 40 or 41 the entity is in VARIABLE-width mode
 *     and group 43 is out of the picture entirely, every span reading its own
 *     40 / 41 with an absent/omitted code meaning the group's documented
 *     default of 0. Otherwise the entity is in CONSTANT-width mode and every
 *     span is group 43. PLINE ▸ Width applied over a whole run writes 43 and
 *     no 40/41 at all, making the constant the COMMON case rather than the
 *     exotic one.
 *
 *     A per-SIDE `||` fallback (what this module did before @codex round 5)
 *     produced neither reading: it applied 43 to every omitted or zero side
 *     while keeping the authored non-zero ones, inventing a mixed taper the
 *     file does not describe — a vertex authored `40 = 3` with no 41 drew a
 *     3 → 43 taper where the reference says 3 → 0. Both the canvas and the
 *     plot consume this one result, so the invention reached paper too.
 *
 *     (`ezdxf.render.trace.TraceBuilder.from_polyline` resolves the same
 *     collision the other way round — a non-zero `const_width` OVERRIDES every
 *     per-vertex pair. CLAUDE.md's source order puts the Autodesk reference
 *     above `ezdxf`, and the reference sentence is explicit about which
 *     channel loses, so the mode is decided by the presence of 40/41.)
 *   - **heavy POLYLINE** — the same per-vertex 40/41 on each `VERTEX` record,
 *     inheriting the POLYLINE record's entity-level `defaultStartWidth` /
 *     `defaultEndWidth` (groups 40 / 41 on the POLYLINE itself) when the
 *     record declares none. There is NO group 43 on a heavy POLYLINE at all,
 *     so this channel has no entity-level MODE to pick: the fallback is
 *     strictly per-record and PRESENCE-based (`??`), and a record that
 *     declares an explicit 0 keeps 0 rather than inheriting the default. The
 *     two channels therefore differ in kind, not merely in which field they
 *     read, and neither rule may be applied to the other's entity.
 *
 *     This channel belongs to the **2D subtype ALONE** (group 70 without bits
 *     8 / 16 / 64), exactly as group 42 does. The DXF group-code tables put
 *     40 / 41 under a shared subclass-marker pair on both records ("Subclass
 *     marker (AcDb2dPolyline or AcDb3dPolyline)"; "Subclass marker
 *     (AcDb2dVertex or AcDb3dPolylineVertex)"), so the TABLE alone does not
 *     say which subclass owns them — the Autodesk CLASS reference does, and
 *     unambiguously: `AcDb2dPolyline` declares `defaultStartWidth` /
 *     `defaultEndWidth` / `constantWidth` and their setters, `AcDb2dVertex`
 *     declares `startWidth` / `endWidth` ("the start width for the vertex …
 *     applies to the polyline segment extending from the current vertex to the
 *     subsequent one"), while `AcDb3dPolyline` declares NO width member at all
 *     and `AcDb3dPolylineVertex`'s ENTIRE method set is `position`,
 *     `setPosition`, `vertexType`. `ezdxf` refuses the band in two independent
 *     places for the same reason — `Polyline.has_width` opens with
 *     `if self.is_2d_polyline:` and otherwise `return False`, and
 *     `render.trace.TraceBuilder.from_polyline` opens its POLYLINE arm with
 *     `if not polyline.is_2d_polyline: raise TypeError("2D POLYLINE
 *     required")`.
 *
 *     Nothing REMOVES a stray 40 / 41 from such a record — the file is
 *     preserved byte for byte and a Properties palette still reads the raw
 *     group off the entity — but the width a 3D polyline or a mesh is DRAWN at
 *     is 0, and that is what this resolver answers. The gate is here rather
 *     than in any one consumer because the alternative is five copies of it:
 *     the canvas adapter, the vector-PDF band, `cad-core`'s drawn shape, its
 *     drawn bounds and `command-engine`'s TRIM write-back all reach the width
 *     through this function and nothing else (@codex PR #785 round 12, F2 —
 *     the adapter arm alone was gated by subtype for its OCS plane but not for
 *     its WIDTH, so a 3D polyline retaining 40 / 41 was sent through the band
 *     builder, which invented an XY-offset ribbon around an arbitrary 3D path).
 *   - **taper** — the pair varies LINEARLY along its span (ezdxf models the
 *     same trapezoid in `ezdxf.render.trace.LinearTrace`). Interpolating it is
 *     the band builder's job, not this module's: here the authored pair is
 *     only READ.
 */

import type { LwpolylineEntity } from "./model";
import { polylineSubtype, type PolylineEntity } from "./entityPolylineTableOle";

/** Per-vertex start/end width pair (DXF group codes 40 / 41). */
export interface PolylineWidth {
  /** Full width where the span leaving this vertex STARTS (group 40). */
  start: number;
  /** Full width where that same span ENDS (group 41). */
  end: number;
}

/** `true` when at least one span is actually wide (an all-zero list is not). */
export function hasPolylineWidth(widths: readonly PolylineWidth[]): boolean {
  return widths.some((w) => w.start !== 0 || w.end !== 0);
}

/** A width group code as a usable number — a non-finite or absent code is 0. */
function widthCode(code: number | undefined): number {
  return Number.isFinite(code) ? (code as number) : 0;
}

/**
 * `true` when this LWPOLYLINE is in VARIABLE-width mode — some vertex declares
 * a non-zero group 40 or 41, which is what makes group 43 "not used" for the
 * WHOLE entity (Autodesk DXF reference, `AcDbPolyline` group 43).
 *
 * Exported because the mode is the entity-level FACT, not an implementation
 * detail of {@link lwpolylineWidths}: a caller that needs to explain which
 * channel a polyline is drawn from (a property inspector, a DXF writer
 * deciding whether to emit 43 at all) must read the same predicate rather than
 * re-derive it and risk disagreeing about the mode.
 */
export function lwpolylineHasVariableWidth(entity: LwpolylineEntity): boolean {
  return entity.vertices.some(
    (v) => widthCode(v.startWidth) !== 0 || widthCode(v.endWidth) !== 0,
  );
}

/**
 * LWPOLYLINE per-vertex widths, resolved in the entity's single width MODE
 * (see the module doc for the reference wording):
 *
 *   - VARIABLE ({@link lwpolylineHasVariableWidth}) — every span is its own
 *     groups 40 / 41, an omitted code being that group's documented default of
 *     0. Group 43 is not read at all.
 *   - CONSTANT — every span is group 43, on both sides.
 *
 * The two are never mixed, so the result is always a faithful reading of ONE
 * channel of the file.
 */
export function lwpolylineWidths(entity: LwpolylineEntity): PolylineWidth[] {
  if (lwpolylineHasVariableWidth(entity)) {
    return entity.vertices.map((v) => ({
      start: widthCode(v.startWidth),
      end: widthCode(v.endWidth),
    }));
  }
  const constant = widthCode(entity.constantWidth);
  return entity.vertices.map(() => ({ start: constant, end: constant }));
}

/**
 * Heavy POLYLINE per-vertex widths, read through the VISIBLE-record `indices`
 * of the run being drawn (a spline-fit polyline's generated run is not its
 * stored record order — `cad-core`'s `heavyPolylineRuns` decides which records
 * those are), each record falling back to the entity-level
 * `defaultStartWidth` / `defaultEndWidth`.
 *
 * `indices` is a plain number list on purpose: it is the same
 * point-type-agnostic contract the heavy-polyline runs themselves carry, so
 * this module needs nothing from `cad-core` and the dependency stays one-way.
 */
/**
 * `true` when this heavy `POLYLINE` declares a width ANYWHERE — i.e. when at
 * least one of its records resolves, through {@link heavyPolylineWidths}' own
 * inheritance rule, to a non-zero group 40 / 41 pair.
 *
 * Exported for the same reason {@link lwpolylineHasVariableWidth} is: the fact
 * "is this entity drawn as a BAND at all?" is an entity-level property of the
 * file, not an implementation detail of the band builder, and a caller that
 * needs to branch on it must read the SAME rule rather than re-derive the
 * `?? defaultStartWidth` fallback and risk disagreeing about the mode.
 *
 * It is composed from the two existing truths rather than re-implemented —
 * every record index, fed through the one width resolver, tested by the one
 * `hasPolylineWidth` predicate — so there is no second copy of the fallback
 * rule to drift. Reading EVERY record (rather than one run's visible indices)
 * makes it a SUPERSET of "some drawn run carries a band": a `false` answer
 * proves no run can declare one, which is the only direction a caller may
 * shortcut on.
 *
 * @codex PR #785 round 10 — `cad-core`'s `heavyPolylineDrawnBounds` gates the
 * drawn-silhouette walk on this, because the walk is 3.2x-4.1x the cost of the
 * model box for a polyline that carries no width at all and the metadata index
 * runs it per entity on 685k-entity drawings.
 */
export function heavyPolylineDeclaresWidth(entity: PolylineEntity): boolean {
  return hasPolylineWidth(
    heavyPolylineWidths(
      entity,
      entity.vertices.map((_v, i) => i),
    ),
  );
}

export function heavyPolylineWidths(
  entity: PolylineEntity,
  indices: readonly number[],
): PolylineWidth[] {
  // THE SUBTYPE GATE (see the module doc for the two references). Groups 40/41
  // are `AcDb2dPolyline` / `AcDb2dVertex` fields; a 3D polyline, a polygon mesh
  // and a poly-face mesh are drawn at width 0 however their records are
  // written. Answered as a ZERO PAIR PER REQUESTED INDEX rather than as an
  // empty list, because every caller zips this result positionally against the
  // run it asked about — an empty list would misalign them instead of
  // narrowing them.
  if (polylineSubtype(entity) !== "2d") {
    return indices.map(() => ({ start: 0, end: 0 }));
  }
  const fallbackStart = entity.defaultStartWidth ?? 0;
  const fallbackEnd = entity.defaultEndWidth ?? 0;
  return indices.map((i) => {
    const v = entity.vertices[i];
    return {
      start: v?.startWidth ?? fallbackStart,
      end: v?.endWidth ?? fallbackEnd,
    };
  });
}
